<template>
  <div :id="mapId" class="map-wrapper"></div>
</template>
<script setup lang="ts">
import { useGoogleMapsApi } from "@/features/google-maps/use-google-maps-api/useGoogleMapsApi";
import { whenever } from "@vueuse/core";

export interface FieldMapProps {
  mapId: string;
  center?: {
    lat: number;
    lng: number;
  };
  pinPosition?: {
    lat: number;
    lng: number;
  };
  draggable?: boolean;
}

interface Events {
  (event: "update:pinPosition", value: { lat: number; lng: number }): void;
}

const props = withDefaults(defineProps<FieldMapProps>(), {
  center: undefined,
  pinPosition: undefined,
  draggable: false,
});
const emit = defineEmits<Events>();

const { isGoogleMapsLoaded, mapsLibrary, markerLibrary } = useGoogleMapsApi();
const map = shallowRef<google.maps.Map>();
const marker = shallowRef<google.maps.marker.AdvancedMarkerElement>();
const isMapRendered = ref(false);
const markerPosition = computed(() => props.pinPosition ?? props.center);

function renderMap() {
  const mapsLibraryConstructor = mapsLibrary.value;

  if (!mapsLibraryConstructor) return;

  if (!props.center) return;
  const mapEl = document.getElementById(props.mapId)!;
  map.value = new mapsLibraryConstructor.Map(mapEl, {
    center: props.center,
    zoom: 18,
    disableDefaultUI: true,
    fullscreenControl: false,
    mapId: props.mapId,
  });
  marker.value = new markerLibrary.value!.AdvancedMarkerElement({
    map: map.value,
    position: markerPosition.value ?? props.center,
    gmpDraggable: props.draggable,
  });
  isMapRendered.value = true;

  if (props.draggable) {
    marker.value.addListener("dragend", (event: google.maps.MapMouseEvent) => {
      const newPosition = event.latLng;
      if (!newPosition) {
        return;
      }

      emit("update:pinPosition", {
        lat: newPosition.lat(),
        lng: newPosition.lng(),
      });
    });
  }
}

whenever(isGoogleMapsLoaded, renderMap);

watch(
  markerPosition,
  (newPosition) => {
    if (!newPosition) {
      return;
    }
    if (marker.value) marker.value.position = newPosition;
  },
  { deep: true }
);

// update location
watch(
  () => props.center,
  () => {
    if (!props.center) {
      return;
    }
    if (!isMapRendered.value) {
      renderMap();
    } else {
      map.value?.setCenter(props.center);
      if (props.pinPosition === undefined) {
        if (marker.value) marker.value.position = props.center;
      }
    }
  },
  { deep: true }
);
</script>

<style scoped>
.map-wrapper {
  width: 100%;
  height: 100%;
}
</style>
